import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
    Container, Typography, Box, Paper, CircularProgress, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
    Select, MenuItem, Card, CardContent, useTheme, IconButton
} from '@mui/material';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { styled } from '@mui/material/styles';
import InsightsIcon from '@mui/icons-material/Insights';
import { ReviewsContext } from '../context/ReviewsContext'; // Assuming timeframe is needed
import { BusinessContext } from '../context/BusinessContext'; // For selected business check
import { colors as chartColors } from '../utils/themeUtils'; // Import colors from themeUtils
import { NoMaxWidthTooltip } from '../components/common/SharedTooltip'; // Add this import
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'; // Import CalendarMonthIcon
import TimelineIcon from '@mui/icons-material/Timeline'; // Import TimelineIcon for consistency, if needed elsewhere
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import TimelineMatrixTable from '../components/dashboard/TimelineMatrixTable';
import { spacing } from '../utils/themeUtils';

// --- NEW: Constants for Sticky Column Widths ---
const CATEGORY_COL_WIDTH = '150px'; // Set to 150px for consistency
const SUBJECT_COL_WIDTH = '150px'; // Represents total rendered width
const TOTAL_COL_WIDTH = '100px'; // Width for the new total column
const MONTH_MIN_WIDTH = '90px'; // Width for month columns
// --- End Constants ---

const API_URL = process.env.REACT_APP_API_URL;

// --- Define Colors (Added) ---
// const colors = {
//     negativeRed: '#f9d1d1', // Light red (matching getRatingColor)
//     positiveGreen: '#d1f9d1' // Light green (matching getRatingColor)
// };
// --- End Define Colors ---

// --- Import Chart Colors --- 
// const colors = {
//     negativeRed: '#f9d1d1', // Light red (matching getRatingColor)
//     positiveGreen: '#d1f9d1' // Light green (matching getRatingColor)
// };
// --- End Define Colors ---

// --- NEW: Function to determine cell background based on comparison to business average --- 
// --- RESTORED and MODIFIED: getCellBackgroundColor function ---
const getCellBackgroundColor = (avgRating, businessRating, isTotalCell = false) => {
    const defaultColor = 'transparent';
    // Use subtle solid RGBA colors for background
    const positiveAlpha = isTotalCell ? 0.5 : 0.10;
    const negativeAlpha = isTotalCell ? 0.5 : 0.10;

    const positiveColor = `rgba(76, 175, 80, ${positiveAlpha})`; // Subtle green background
    const negativeColor = `rgba(244, 67, 54, ${negativeAlpha})`; // Subtle red background

    if (avgRating === null || avgRating === undefined || businessRating === null || businessRating === undefined) {
        return defaultColor;
    }

    // Cells >= average rating should be green, cells < average rating should be red
    if (avgRating >= businessRating) {
        return positiveColor; // Use subtle green
    } else {
        return negativeColor; // Use subtle red
    }
};

// Timeframe options (copied from Header.js for consistency)
// const timeframeOptions = [
//  { value: "7", label: "Last 7 Days" },
//  { value: "30", label: "Last 30 Days" },
//  { value: "90", label: "Last 90 Days" },
//  { value: "365", label: "Last 365 Days" },
//  { value: "all", label: "All Time" },
//];

// Function to determine cell background color based on rating
const getRatingColor = (rating) => {
    if (rating === null || rating === undefined) return '#ffffff'; // Default white for no data
    if (rating >= 4.5) return '#d1f9d1'; // Light green
    if (rating >= 4.0) return '#e8f9d1';
    if (rating >= 3.5) return '#f9f4d1';
    if (rating >= 3.0) return '#f9e4d1';
    if (rating >= 2.0) return '#f9d1d1'; // Light red
    return '#f9c0c0'; // Darker red
};

// --- Tooltip Content Component --- 
const CellTooltipContent = ({ ratings, businessRating, category, subject }) => {
    if (!ratings || ratings.length === 0) {
        return null; // Should not happen if called correctly, but good practice
    }

    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    ratings.forEach(r => {
        if (counts[r] !== undefined) {
            counts[r]++;
        }
    });

    // Define colors based on themeUtils (already imported as chartColors)
    const positiveColor = chartColors.positiveGreen; // e.g., '#4CAF50'
    const negativeColor = chartColors.negativeRed;   // e.g., '#F44336'
    const neutralColor = 'text.secondary'; // Use theme secondary text color for grey

    return (
        <Box sx={{ textAlign: 'left' }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Ratings:</Typography><br />
            {[1, 2, 3, 4, 5].map(star => {
                let countColor = neutralColor; // Default to neutral

                // --- NEW: Check for zero count first ---
                if (counts[star] === 0) {
                    countColor = neutralColor; // Keep zero counts neutral
                } else if (businessRating !== null && businessRating !== undefined) {
                    // Apply coloring only if count is not zero
                    if (star > businessRating) {
                        countColor = positiveColor;
                    } else if (star < businessRating) {
                        countColor = negativeColor;
                    }
                    // Implicitly neutral if star == businessRating
                }
                // --- End NEW check ---

                return (
                    <React.Fragment key={`tooltip-star-${star}-${category || ''}-${subject || ''}`}>
                        <Typography variant="caption">
                            <span style={{ color: neutralColor }}>{star}★: </span>
                            <span style={{ fontWeight: 'bold', color: countColor }}>{counts[star]}</span>
                        </Typography><br />
                    </React.Fragment>
                );
            })}
        </Box>
    );
};



// --- Sorting Helper Functions ---
const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1]; // Fallback to original index for stability
    });
    return stabilizedThis.map((el) => el[0]);
};

const getComparator = (order, orderBy, secondaryOrderBy = null, secondaryOrder = 'asc') => {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy, secondaryOrderBy, secondaryOrder)
        : (a, b) => -descendingComparator(a, b, orderBy, secondaryOrderBy, secondaryOrder);
};

const descendingComparator = (a, b, orderBy, secondaryOrderBy, secondaryOrder) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    if (orderBy === 'totalData.avg') {
        aValue = a.totalData?.avg;
        bValue = b.totalData?.avg;
    } else if (orderBy === 'category' || orderBy === 'subject') { // Alphabetical for category/subject names
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
    }
    // Handle undefined/null for numeric sort (treat as lowest or highest based on direction)
    const orderDirection = secondaryOrderBy ? (secondaryOrder === 'asc' ? 1 : -1) : (getComparator.caller?.name.includes('desc') ? -1 : 1);

    if (aValue === null || typeof aValue === 'undefined') aValue = orderDirection === 1 ? -Infinity : Infinity;
    if (bValue === null || typeof bValue === 'undefined') bValue = orderDirection === 1 ? -Infinity : Infinity;


    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;

    // Secondary sort
    if (secondaryOrderBy) {
        let secAValue = a[secondaryOrderBy];
        let secBValue = b[secondaryOrderBy];
        if (secondaryOrderBy === 'category' || secondaryOrderBy === 'subject') {
            secAValue = String(secAValue || '').toLowerCase();
            secBValue = String(secBValue || '').toLowerCase();
        }
        
        if (secAValue === null || typeof secAValue === 'undefined') secAValue = secondaryOrder === 'asc' ? -Infinity : Infinity;
        if (secBValue === null || typeof secBValue === 'undefined') secBValue = secondaryOrder === 'asc' ? -Infinity : Infinity;


        if (secondaryOrder === 'asc') {
            if (secBValue < secAValue) return -1;
            if (secBValue > secAValue) return 1;
        } else { // desc
            if (secBValue > secAValue) return -1;
            if (secBValue < secAValue) return 1;
        }
    }
    return 0;
};

function SubjectMatrixPage() {
    const [matrixDataInternal, setMatrixDataInternal] = useState([]); // Raw data from API
    const [groupedMonths, setGroupedMonths] = useState({}); // State for { year: [months] }
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const { selectedBusinesses } = useContext(BusinessContext);
    const {
        timeframe,
        selectedDateRange,
        selectedSources
    } = useContext(ReviewsContext);
    const [expandedCategories, setExpandedCategories] = useState({}); // { categoryName: { isLoading: bool, subjects: [] } }
    const tableContainerRef = useRef(null); // Create the ref for the TableContainer
    const prevIsSubjectColumnGlobalRef = useRef(); // --- NEW: Ref to track previous global subject column visibility
    
    // --- NEW: Cache for subject data to avoid redundant API calls ---
const subjectDataCache = useRef(new Map()); // Map<cacheKey, subjectData>

// Note: Subject limiting is now handled by backend based on impact, no frontend limit needed

// --- NEW: Function to generate cache key ---
const generateCacheKey = (categoryName, businessIds, timeframe, selectedDateRange, selectedSources) => {
    const dateKey = selectedDateRange && selectedDateRange.start && selectedDateRange.end 
        ? `${selectedDateRange.start.toISOString().split('T')[0]}-${selectedDateRange.end.toISOString().split('T')[0]}`
        : timeframe || 'all';
    const sourcesKey = selectedSources && selectedSources.length > 0 && !selectedSources.includes("all") ? selectedSources.sort().join(',') : 'all';
    return `${categoryName}-${businessIds.sort().join(',')}-${dateKey}-${sourcesKey}`;
};

    // --- NEW: Sorting State ---
    const [sortConfig, setSortConfig] = useState({ key: 'totalData.avg', direction: 'desc' }); // Default sort

    const handleSortRequest = (property) => {
        const isAsc = sortConfig.key === property && sortConfig.direction === 'asc';
        setSortConfig({ key: property, direction: isAsc ? 'desc' : 'asc' });
    };

    // Determine if the Subject Group column should be globally visible
    const isSubjectColumnGlobal = useMemo(() => {
        // Now only depends on whether any category has expanded subjects
        return Object.values(expandedCategories).some(cat => cat?.subjects?.length > 0);
    }, [expandedCategories]);

    useEffect(() => {
        const fetchData = async () => {
            if (!selectedBusinesses || selectedBusinesses.length === 0) {
                setError("Please select a business first.");
                setMatrixDataInternal([]);
                setGroupedMonths({});
                setExpandedCategories({}); // Reset expanded state
                setIsLoading(false);
                return;
            }
            
            const businessIds = selectedBusinesses.map(b => b.id); 
            
            setIsLoading(true);
            setError(null);
            setExpandedCategories({}); // Reset expanded state on new fetch
            
            // --- NEW: Clear cache when business selection or timeframe changes ---
            subjectDataCache.current.clear();
            
            try {
                let url = `${API_URL}/analysis/matrix-data`;
                const params = new URLSearchParams();
                businessIds.forEach(id => params.append('selected_business_ids', id));
                
                // UPDATED: Check for custom date range first, then timeframe
                if (selectedDateRange && selectedDateRange.start && selectedDateRange.end) {
                    params.set('start_date', selectedDateRange.start.toISOString().split('T')[0]);
                    params.set('end_date', selectedDateRange.end.toISOString().split('T')[0]);
                } else if (timeframe && timeframe !== 'all') {
                    const parsedTimeframe = parseInt(timeframe, 10);
                    if (!isNaN(parsedTimeframe)) {
                        const startDate = new Date(Date.now() - parsedTimeframe * 24 * 60 * 60 * 1000).toISOString();
                        params.set('start_date', startDate);
                    }
                }
                // Add source filtering - only if not "all" and not empty
                if (selectedSources && selectedSources.length > 0 && !selectedSources.includes("all")) {
                    selectedSources.forEach(source => params.append('source', source));
                }
                // Always fetch category level data initially for the main table
                params.set('aggregation_level', 'category'); 
                const paramString = params.toString();
                if (paramString) url += `?${paramString}`;

                const response = await fetch(url, { 
                    method: 'GET', 
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ detail: response.statusText }));
                    throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
                }
                const apiResponse = await response.json(); 

                if (!apiResponse || !apiResponse.matrixRenderData || apiResponse.matrixRenderData.length === 0) {
                    setMatrixDataInternal([]);
                    setGroupedMonths({});
                    return;
                }

                setMatrixDataInternal(apiResponse.matrixRenderData);
                setGroupedMonths(apiResponse.groupedMonths);

            } catch (err) {
                setError(err.message || "Failed to load initial matrix data.");
                setMatrixDataInternal([]); // Clear data on error
                setGroupedMonths({});    // Clear data on error
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [selectedBusinesses, timeframe, selectedDateRange, selectedSources]); // aggregationLevel state will manage re-renders if toggle is used internally

    // --- Sorted Matrix Data (Categories) ---
    const sortedMatrixRenderData = useMemo(() => {
        if (!matrixDataInternal) return [];
        let dataToSort = [...matrixDataInternal];
        if (sortConfig.key) {
            // Primary sort by the configured key (e.g., 'totalData.avg')
            // Secondary sort by category name alphabetically
            dataToSort = stableSort(dataToSort, getComparator(sortConfig.direction, sortConfig.key, 'category', 'asc'));
        } else {
             // Default sort: alphabetically by category if no specific sortConfig.key
             dataToSort = stableSort(dataToSort, getComparator('asc', 'category'));
        }
        return dataToSort;
    }, [matrixDataInternal, sortConfig]);

    // Function to toggle category expansion and fetch subjects if needed
    const handleToggleCategory = async (categoryName) => {
        const isCurrentlyExpanded = !!expandedCategories[categoryName]?.subjects;

        if (isCurrentlyExpanded) {
            setExpandedCategories(prev => ({ ...prev, [categoryName]: undefined })); // Collapse
            return; 
        }

        // --- NEW: Check cache first ---
        const businessIds = selectedBusinesses.map(b => b.id);
        const cacheKey = generateCacheKey(categoryName, businessIds, timeframe, selectedDateRange, selectedSources);
        const cachedData = subjectDataCache.current.get(cacheKey);
        
        if (cachedData) {
            // Use cached data (already selected by impact and sorted by rating from backend)
            let sortedSubjectData = [...cachedData.subjects];
            
            // Apply frontend sorting if different from backend's rating sort
            if (sortConfig.key === 'totalData.avg') {
                sortedSubjectData = stableSort(
                    sortedSubjectData, 
                    getComparator(sortConfig.direction, 'totalData.avg', 'subject', 'asc')
                );
            }
            
            setExpandedCategories(prev => ({
                ...prev,
                [categoryName]: { 
                    isLoading: false, 
                    subjects: sortedSubjectData,
                    totalSubjects: cachedData.totalFound,
                    isLimited: cachedData.totalReturned < cachedData.totalFound
                }
            }));
            
            return;
        }

        // Expand: set loading state and fetch
        setExpandedCategories(prev => ({ ...prev, [categoryName]: { isLoading: true, subjects: [] } }));

        try {
            let url = `${API_URL}/analysis/matrix-data/subjects`;
            const params = new URLSearchParams();
            params.set('category_name', categoryName);
            
            // Reuse business IDs from selectedBusinesses to avoid redundant queries
            if (selectedBusinesses && selectedBusinesses.length > 0) {
                selectedBusinesses.forEach(business => params.append('selected_business_ids', business.id));
            }
            
            // UPDATED: Check for custom date range first, then timeframe
            if (selectedDateRange && selectedDateRange.start && selectedDateRange.end) {
                params.set('start_date', selectedDateRange.start.toISOString().split('T')[0]);
                params.set('end_date', selectedDateRange.end.toISOString().split('T')[0]);
            } else if (timeframe && timeframe !== 'all') {
                const parsedTimeframe = parseInt(timeframe, 10);
                if (!isNaN(parsedTimeframe)) {
                    const startDate = new Date(Date.now() - parsedTimeframe * 24 * 60 * 60 * 1000).toISOString();
                    params.set('start_date', startDate);
                }
            }

            // Add source filtering - only if not "all" and not empty
            if (selectedSources && selectedSources.length > 0 && !selectedSources.includes("all")) {
                selectedSources.forEach(source => params.append('source', source));
            }

            const paramString = params.toString();
            if (paramString) url += `?${paramString}`;

            const response = await fetch(url, { 
                method: 'GET', 
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ detail: response.statusText }));
                throw new Error(errorData.detail || `HTTP error fetching subjects for ${categoryName}`);
            }
            const responseData = await response.json(); // Expects SubjectsMatrixResponse
            
            // --- Cache the response data ---
            subjectDataCache.current.set(cacheKey, responseData);

            // --- Backend already selected by impact and sorted by rating ---
            let sortedSubjectData = responseData.subjects || [];
            
            // Apply frontend sorting if different from backend's rating sort
            if (sortConfig.key === 'totalData.avg') {
                sortedSubjectData = stableSort(
                    sortedSubjectData, 
                    getComparator(sortConfig.direction, 'totalData.avg', 'subject', 'asc')
                );
            }
            
            setExpandedCategories(prev => ({
                ...prev,
                [categoryName]: { 
                    isLoading: false, 
                    subjects: sortedSubjectData,
                    totalSubjects: responseData.totalFound,
                    isLimited: responseData.totalReturned < responseData.totalFound
                }
            }));

        } catch (err) {
            setExpandedCategories(prev => ({
                ...prev,
                [categoryName]: { isLoading: false, subjects: [], error: err.message }
            }));
        }
    };

    // --- NEW: Memoized and Sorted Expanded Categories for Display ---
    const displayableExpandedCategories = useMemo(() => {
        const newExpanded = {};
        for (const categoryName in expandedCategories) {
            const categoryState = expandedCategories[categoryName];
            if (categoryState && categoryState.subjects && categoryState.subjects.length > 0) {
                // No need to re-sort here since subjects are already sorted and limited
                // in handleToggleCategory. Just keep the existing subjects array.
                newExpanded[categoryName] = categoryState;
            } else {
                // If no subjects or other state, keep as is
                newExpanded[categoryName] = categoryState;
            }
        }
        return newExpanded;
    }, [expandedCategories]);

    // --- useEffect to scroll table right on load (Keep) ---
    useEffect(() => {
        // Only scroll when loading is finished and the ref is attached
        if (!isLoading && tableContainerRef.current && sortConfig.key === 'totalData.avg') { // Only auto-scroll if sorted by total initially
            // Scroll to the far right
            tableContainerRef.current.scrollLeft = tableContainerRef.current.scrollWidth;
        }
        // Run this effect when isLoading changes or sortConfig changes
    }, [isLoading, sortConfig.key]);

    // --- NEW: useEffect to adjust scroll when subject column appears ---
    useEffect(() => {
        if (tableContainerRef.current && !isLoading) {
            const subjectColWidthNumeric = parseInt(SUBJECT_COL_WIDTH, 10); // SUBJECT_COL_WIDTH is '150px'
            if (isNaN(subjectColWidthNumeric)) return; // Exit if width is not a number

            if (isSubjectColumnGlobal && !prevIsSubjectColumnGlobalRef.current) {
                // Subject column just appeared, adjust scroll to compensate for its width
                setTimeout(() => {
                    if (tableContainerRef.current) { 
                        tableContainerRef.current.scrollLeft += subjectColWidthNumeric;
                    }
                }, 0); 
            } else if (!isSubjectColumnGlobal && prevIsSubjectColumnGlobalRef.current) {
                // Subject column just disappeared, adjust scroll back
                setTimeout(() => {
                    if (tableContainerRef.current) {
                        tableContainerRef.current.scrollLeft -= subjectColWidthNumeric;
                        // Ensure scrollLeft doesn't go negative
                        if (tableContainerRef.current.scrollLeft < 0) {
                            tableContainerRef.current.scrollLeft = 0;
                        }
                    }
                }, 0);
            }
        }
        // Update the ref after checking, so it holds the value from the previous render during the check for the next run.
        prevIsSubjectColumnGlobalRef.current = isSubjectColumnGlobal;
    }, [isSubjectColumnGlobal, isLoading]); // Dependencies: run when these change.

    // Calculate the average rating of selected businesses
    const averageBusinessRating = useMemo(() => {
        const numSelected = selectedBusinesses?.length || 0;
        if (numSelected === 0) return undefined; // Use undefined to match previous logic's default

        const businessesWithRating = selectedBusinesses.filter(biz => biz.rating !== null && biz.rating !== undefined);
        if (businessesWithRating.length === 0) return undefined;

        const sumRatings = businessesWithRating.reduce((sum, biz) => sum + biz.rating, 0);
        return sumRatings / businessesWithRating.length;
    }, [selectedBusinesses]);

    // Keep month minWidth fixed (Keep)
    const monthMinWidth = MONTH_MIN_WIDTH; 

    return (
        <Box sx={{ // START: Add Outer Box Wrapper
            minHeight: '100%', // Or 'auto' depending on desired behavior
            background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
            display: 'flex',
            flexDirection: 'column',
            pt: spacing.pageTop, // Standard top padding
            pb: spacing.pageBottom, // Standard bottom padding
            px: spacing.pageHorizontal, // Standard horizontal padding
            opacity: 0,
            animation: 'fadeIn 1s forwards',
            '@keyframes fadeIn': {
                from: { opacity: 0 },
                to: { opacity: 1 },
            },
        }}> 
        <Container maxWidth={false} sx={{ 
            // pt: 4, // Remove pt, handled by outer Box py
            pb: 4, 
            // height: '100%', // Height management might need adjustment based on outer box
            flex: 1, // Allow container to grow within the flex box
            display: 'flex', 
            flexDirection: 'column' 
        }}> {/* Adjust height based on header/padding */}
             {/* START: Header Section Update */}
             <Box sx={{ 
                mb: { xs: 2, lg: 4 },
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 2, md: 3 },
                alignItems: { xs: 'flex-start', md: 'flex-start' }, // Align items to start like Reviews.js
                width: '100%',
                height: { xs: 'auto', md: 'auto' } // Adjust height as needed, or remove if fixed height isn't desired
              }}>
                 {/* First Column: Title and Description */}
                 <Box sx={{ 
                     flex: { xs: '1 1 100%', md: '1 1 33.33%' },
                     px: spacing.headerHorizontal,
                     height: { xs: 'auto', md: '100%' }
                 }}>
                    <Typography
                        variant="h3"
                        component="h1"
                        sx={{
                            fontWeight: 800,
                            fontSize: { xs: '1.75rem', lg: '2.5rem' },
                            color: '#0A2540',
                            letterSpacing: '-0.02em',
                            mb: { xs: 1, lg: 2 }, // Match Reviews.js margin
                            display: 'flex', // Make flex to align icon
                            alignItems: 'center',
                            gap: { xs: 1, lg: 2 } // Match Reviews.js gap
                        }}
                    >
                        <CalendarMonthIcon sx={{ fontSize: 'inherit', color: '#1976d2' }} /> {/* Match icon styling */}
                        Timeline
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#546E7A',
                            fontSize: { xs: '0.95rem', lg: '1.1rem' } // Match Reviews.js font size
                        }}
                    >
                        Track average review ratings over time, broken down by category or subject.
                    </Typography>
                 </Box>

                 {/* Second Column: Placeholder (like Reviews.js) */}
                 <Box sx={{ 
                    flex: { xs: '1 1 100%', md: '1 1 33.33%' },
                    px: spacing.headerHorizontal,
                    height: { xs: 'auto', md: '100%' },
                    display: { xs: 'none', md: 'block' } // Hide on mobile if not used
                  }}>
                     {/* Add content here if needed, otherwise acts as a spacer */}
                  </Box>

                 {/* Third Column: Placeholder (like Reviews.js) */}
                 <Box sx={{ 
                    flex: { xs: '1 1 100%', md: '1 1 33.33%' },
                    px: spacing.headerHorizontal,
                    height: { xs: 'auto', md: '100%' },
                    display: { xs: 'none', md: 'block' } // Hide on mobile if not used
                  }}>
                     {/* Add content here if needed, otherwise acts as a spacer */}
                  </Box>
             </Box>
             {/* END: Header Section Update */}
            {/* Start Card Wrapper */} 
            <Card sx={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                borderRadius: { xs: '12px', sm: '16px', md: '24px' },
                border: '1px solid rgba(25, 118, 210, 0.1)',
                boxShadow: '0 20px 40px -12px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                    boxShadow: '0 24px 48px -12px rgba(0,0,0,0.15)'
                },
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #1976d2, #42a5f5)'
                },
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1, // ADDED: Allow Card to grow vertically
                minHeight: 0 // ADDED: Prevent potential overflow issues in flex containers
            }}>
            <CardContent sx={{ 
                p: { xs: 1, sm: 1.5, md: 2 }, // Standard padding
                '&:last-child': { pb: { xs: 1, sm: 1.5, md: 2 } },
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1, // ADDED: Allow CardContent to grow vertically
                minHeight: 0 // ADDED: Prevent potential overflow issues in flex containers
             }}>
                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                        <CircularProgress />
                    </Box>
                )}
                {error && !isLoading && (
                     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                        <Alert severity="error" sx={{width: '100%'}}>{error}</Alert>
                     </Box>
                )}
                {!isLoading && !error && sortedMatrixRenderData.length === 0 && ( 
                     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                        <Alert severity="info">No subject data available for the selected business or timeframe.</Alert>
                     </Box>
                )}
                
                {!isLoading && !error && sortedMatrixRenderData.length > 0 && ( 
               <TableContainer
                        ref={tableContainerRef}
                   sx={{ 
                       overflow: 'auto', 
                            flexGrow: 1,
                            minHeight: 0,
                       mt: { xs: 0.5, sm: 1 } 
                   }}> 
                        <TimelineMatrixTable
                            matrixRenderData={sortedMatrixRenderData}
                            groupedMonths={groupedMonths}
                            expandedCategories={displayableExpandedCategories}
                            isSubjectColumnGlobal={isSubjectColumnGlobal}
                            averageBusinessRating={averageBusinessRating}
                            handleToggleCategory={handleToggleCategory}
                            monthMinWidth={monthMinWidth}
                            timeframe={timeframe}
                            // --- NEW: Pass sort props ---
                            sortConfig={sortConfig}
                            onSortRequest={handleSortRequest}
                        />
                </TableContainer>
                )}
            </CardContent>
           </Card> 
           {/* End Card Wrapper */} 
        </Container>
        </Box> // END: Add Outer Box Wrapper
    );
}

export default SubjectMatrixPage; 